import { saveCustomerJourneyCookie } from '@cinch-labs/customer-journey-cookies'
import { Heading } from '@cinch-labs/design-system'
import { SsrExperimentsProvider } from '@cinch-nx/adobe-server-side'
import { useStoryblokState } from '@cinch-nx/data-storyblok'
import { Env, readFromEnv } from '@cinch-nx/environments'
import { Head, StoryblokRenderer } from '@cinch-nx/landing-ui-shared'
import { Wrapper } from '@cinch-nx/shared-ui'
import {
  OurCommitmentCarousel,
  ShopByShape,
  USPContainer,
  WeBuyAnyCar,
} from './components'
import { NewCustomerTabs } from './components/new-customer-tabs'
import { RecentAndRecommendedVehicles } from './components/recent-and-recommended-vehicles'
import { ReturningCustomerTabs } from './components/returning-customer-tabs'
import { storyblokComponents } from './data/storyblok-renderer-map'
import type { HomepageProps } from './get-server-side-props'
import styles from './homepage.module.css'
import homePage from './locale/index'
import { HeroAdditionalProps } from './components/hero-v2/hero'

export const LandingFeatureHomePage = ({
  preview,
  vehiclesData,
  trustpilotData,
  story,
  usingStoryFallback,
  showPersonalisedComponents,
  recentlyViewedListings,
  similarListings,
  pageMeta,
  searchResponse,
}: HomepageProps) => {
  story = useStoryblokState({
    story: story ?? undefined,
    enableBridge: preview,
  })
  const { ourCommitmentSlides, wbac, seo } = homePage

  if (preview && story.content.previewPersonalisedContent) {
    showPersonalisedComponents = story.content.previewPersonalisedContent
  }

  const personalisedContent = story?.content?.personalised?.[0]
  saveCustomerJourneyCookie()

  const isProd = process.env['STAGE_NAME'] === 'production'
  // TODO: uncomment when continue with favoriting on home when A&E is back
  // const showFavouritingTab =
  //   readFromEnv(Env.EnableFavouritingHomePageChanges) === 'true'
  // const { status: userStatus } = useUserStore()
  // const { favourites } = useFavouritesStore()
  // this console.logs are required in the ticket to prove we are getting favourites and user Status.
  // console.log('userStatus', userStatus)
  // console.log('favourites', favourites)

  const newCarsIsEnabled = readFromEnv(Env.EnableNewCars) === 'true'

  const heroData: HeroAdditionalProps = {
    heroFormAdditionalData: {
      makes: vehiclesData.makes,
      totalStock: vehiclesData.totalStock,
      starRating: trustpilotData.stars,
      starRatingLabel: trustpilotData.starRatingLabel,
      numberOfReviews: trustpilotData.total,
      searchResponse,
    },
    storyFallback: usingStoryFallback,
  }

  return (
    <SsrExperimentsProvider experiments={pageMeta.experiments}>
      <Head
        {...story}
        fallbackTitle={seo.title}
        fallbackDescription={seo.description}
      />
      {story.content.herov2 && (
        <StoryblokRenderer
          block={story.content.herov2}
          components={storyblokComponents}
          props={{ ...heroData }}
        />
      )}

      {story.content.usp && <USPContainer slides={story.content.usp} />}

      {/* TODO: uncomment when continue with favouriting on home when A&E is back */}
      {/* {showFavouritingTab && (
        <div className={styles['favouritingTabText']}>
          Favouriting feature is enabled!
        </div>
      )} */}
      {newCarsIsEnabled &&
        !showPersonalisedComponents &&
        story.content.usedCarsCarouselTab &&
        story.content.newCarsCarouselTab && (
          <Wrapper className={{ inner: styles['newUserTabbedCarousel'] }}>
            <NewCustomerTabs
              usedCarsCarouselTab={story?.content?.usedCarsCarouselTab}
              newCarsCarouselTab={story?.content?.newCarsCarouselTab}
            />
          </Wrapper>
        )}
      {showPersonalisedComponents ? (
        <Wrapper>
          <Heading element="h2" className={styles['heading']}>
            Welcome back
          </Heading>
          {newCarsIsEnabled && story.content.newCarsCarouselTab ? (
            <ReturningCustomerTabs
              newCarsTab={story.content.newCarsCarouselTab}
            />
          ) : (
            <RecentAndRecommendedVehicles
              recentlyViewedListings={recentlyViewedListings ?? []}
              similarListings={similarListings ?? []}
            />
          )}

          {personalisedContent.valuePropositions && (
            <div className={styles['valuePropsCardsContainer']}>
              <StoryblokRenderer
                block={personalisedContent.valuePropositions}
                components={storyblokComponents}
              />
            </div>
          )}

          {personalisedContent.helpMeChoose && (
            <StoryblokRenderer
              block={personalisedContent.helpMeChoose}
              components={storyblokComponents}
            />
          )}
        </Wrapper>
      ) : null}
      <ShopByShape count={vehiclesData.carTypes} />

      {!showPersonalisedComponents && story.content.whyCinch && (
        <StoryblokRenderer
          block={story.content.whyCinch}
          components={storyblokComponents}
        />
      )}

      {story.content.partEx && (
        <StoryblokRenderer
          block={story.content.partEx}
          components={storyblokComponents}
        />
      )}

      {newCarsIsEnabled && story.content.carsCarousel && (
        <Wrapper>
          <StoryblokRenderer
            block={story.content.carsCarousel}
            components={storyblokComponents}
          />
        </Wrapper>
      )}

      {!showPersonalisedComponents ? (
        <OurCommitmentCarousel {...ourCommitmentSlides} />
      ) : null}

      <WeBuyAnyCar {...wbac} />

      {isProd ? (
        <img
          alt="ZeroFox"
          className={styles['zeroFox']}
          src="https://d21y75miwcfqoq.cloudfront.net/786a6533"
        />
      ) : null}
    </SsrExperimentsProvider>
  )
}

export default LandingFeatureHomePage
